import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, UserIcon, MailIcon, PhoneIcon, LinkedinIcon, BuildingIcon, MapPinIcon, DollarSignIcon, GlobeIcon, StarIcon, MonitorIcon, BriefcaseIcon, LockIcon, ArrowRightIcon } from 'lucide-react';
const blurredFields = [{
  icon: UserIcon,
  label: 'Owner name',
  value: 'Jonathan Smitherson'
}, {
  icon: MailIcon,
  label: 'Email',
  value: 'jonathan.s@business.com'
}, {
  icon: PhoneIcon,
  label: 'Direct phone',
  value: '(512) 555-0198'
}, {
  icon: LinkedinIcon,
  label: 'LinkedIn',
  value: 'linkedin.com/in/jsmith'
}, {
  icon: BriefcaseIcon,
  label: 'Title',
  value: 'Founder & CEO'
}, {
  icon: MapPinIcon,
  label: 'Address',
  value: '123 Main St, Austin TX'
}, {
  icon: DollarSignIcon,
  label: 'Revenue',
  value: '$1.2M - $5M'
}, {
  icon: BuildingIcon,
  label: 'Employees',
  value: '11-50 employees'
}, {
  icon: GlobeIcon,
  label: 'Website',
  value: 'www.businesssite.com'
}, {
  icon: MonitorIcon,
  label: 'Tech stack',
  value: 'Shopify, Toast, Mailchimp'
}, {
  icon: StarIcon,
  label: 'Google rating',
  value: '4.8 (124 reviews)'
}];
export function OwnerFinderSearch() {
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchedName, setSearchedName] = useState('');
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setIsLoading(true);
    setTimeout(() => {
      setSearchedName(query);
      setIsLoading(false);
    }, 800);
  };
  const displayName = searchedName || "Joe's Pizza";
  return <section className="py-20 bg-white border-b border-slate-100" data-id="element-6062">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6063">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start" data-id="element-6064">
          {/* Left: Title + Search */}
          <div className="lg:col-span-5 lg:sticky lg:top-32" data-id="element-6065">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 leading-tight" data-id="element-6066">
              Find any local business{' '}
              <span className="text-blue-600" data-id="element-6067">owner</span>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8" data-id="element-6068">
              Search 200M+ local businesses and get verified owner contact data
              instantly.
            </p>

            <form onSubmit={handleSearch} className="mb-8" data-id="element-6069">
              <div className="flex gap-2" data-id="element-6070">
                <div className="flex-1 flex items-center bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus-within:border-blue-300 focus-within:ring-2 focus-within:ring-blue-100 transition-all" data-id="element-6071">
                  <SearchIcon className="w-5 h-5 text-slate-400 mr-3 shrink-0" data-id="element-6072" />
                  <input type="text" placeholder="Business name..." value={query} onChange={e => setQuery(e.target.value)} required className="w-full bg-transparent border-none text-slate-900 placeholder:text-slate-400 text-base outline-none" data-id="element-6073" />
                </div>
                <button type="submit" disabled={isLoading || !query.trim()} className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shrink-0" data-id="element-6074">
                  {isLoading ? <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-5 h-5 border-2 border-white border-t-transparent rounded-full" data-id="element-6075" /> : 'Search'}
                </button>
              </div>
            </form>

            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100" data-id="element-6076">
              <div className="flex items-center gap-3 mb-3" data-id="element-6077">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center" data-id="element-6078">
                  <LockIcon className="w-5 h-5 text-blue-600" data-id="element-6079" />
                </div>
                <div data-id="element-6080">
                  <div className="text-sm font-bold text-slate-900" data-id="element-6081">
                    Unlock full contact data
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-6082">
                    50+ verified fields per business
                  </div>
                </div>
              </div>
              <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="mt-3 w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all text-sm" data-id="element-6083">
                Start free <ArrowRightIcon className="w-4 h-4" data-id="element-6084" />
              </a>
            </div>
          </div>

          {/* Right: Results Card */}
          <div className="lg:col-span-7" data-id="element-6085">
            <AnimatePresence mode="wait" data-id="element-6086">
              <motion.div key={displayName} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4
            }} className="bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden" data-id="element-6087">
                {/* Result Header */}
                <div className="bg-white px-6 py-4 border-b border-slate-200 flex items-center justify-between" data-id="element-6088">
                  <div data-id="element-6089">
                    <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2" data-id="element-6090">
                      {displayName}
                      {searchedName && <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-6091">
                          Match
                        </span>}
                    </h3>
                  </div>
                  <div className="text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full flex items-center gap-1" data-id="element-6092">
                    <LockIcon className="w-3 h-3" data-id="element-6093" />
                    50+ fields
                  </div>
                </div>

                {/* Blurred Fields */}
                <div className="p-6" data-id="element-6094">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-6095">
                    {blurredFields.map((field, idx) => {
                    const Icon = field.icon;
                    return <motion.div key={idx} initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} transition={{
                      delay: idx * 0.04,
                      duration: 0.3
                    }} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-slate-100" data-id="element-6096">
                          <Icon className="w-4 h-4 text-slate-400 shrink-0" data-id="element-6097" />
                          <div className="flex-1 min-w-0" data-id="element-6098">
                            <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-6099">
                              {field.label}
                            </div>
                            <div className="text-sm font-medium text-slate-700 blur-[5px] select-none truncate" data-id="element-6100">
                              {field.value}
                            </div>
                          </div>
                        </motion.div>;
                  })}
                  </div>

                  {/* Inline CTA */}
                  <div className="mt-4 text-center" data-id="element-6101">
                    <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-600 hover:text-blue-700 transition-colors" data-id="element-6102">
                      Sign up to reveal all fields{' '}
                      <ArrowRightIcon className="w-4 h-4" data-id="element-6103" />
                    </a>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>;
}